import type { Asset, Stored } from "@overmap-ai/common"
import * as turf from "@turf/turf"
import type { CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification } from "mapbox-gl"
import { memo, useMemo } from "react"

import { useAppSelector } from "../../store"
import { selectMapStyle } from "../../store/selectors"
import { Layer, Source } from "../components"
import {
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { mapStyleColorMapping } from "../mappings"

interface AssetMarkerProps {
	asset: Stored<Asset>
}

export const AssetMarker = memo<AssetMarkerProps>((props) => {
	const { asset } = props

	const mapStyle = useAppSelector(selectMapStyle)
	const mapStyleColor = mapStyleColorMapping[mapStyle]

	const assetCircleLayerId = `${asset.uuid}-circle`
	const assetLineLayerId = `${asset.uuid}-line`
	const assetFillLayerId = `${asset.uuid}-fill`

	const geoJson = useMemo(() => {
		return turf.feature(asset.geometry)
	}, [asset.geometry])

	const assetCircleLayerPaint = useMemo(() => {
		return {
			"circle-radius": 4,
			"circle-color": mapStyleColor,
		} satisfies CircleLayerSpecification["paint"]
	}, [mapStyleColor])

	const assetLineLayerPaint = useMemo(() => {
		return {
			"line-width": 4,
			"line-color": mapStyleColor,
			"line-opacity": 0.4,
		} satisfies LineLayerSpecification["paint"]
	}, [mapStyleColor])

	const assetFillLayerPaint = useMemo(() => {
		return {
			"fill-color": mapStyleColor,
			"fill-opacity": 0.4,
			"fill-outline-color": mapStyleColor,
		} satisfies FillLayerSpecification["paint"]
	}, [mapStyleColor])

	return (
		<Source type="geojson" data={geoJson} promoteId="uuid">
			<Layer
				id={assetCircleLayerId}
				type="circle"
				paint={assetCircleLayerPaint}
				filter={POINT_GEOMETRY_TYPE_FILTER}
			/>
			<Layer
				id={assetLineLayerId}
				type="line"
				paint={assetLineLayerPaint}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
			/>
			<Layer
				id={assetFillLayerId}
				type="fill"
				paint={assetFillLayerPaint}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
			/>
		</Source>
	)
})

AssetMarker.displayName = "AssetMarker"
